import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { LandingLayout } from '../landing/shared/LandingLayout';
import { SEOHead } from '../landing/shared/SEOHead';
import { BUSINESS_REGISTER_URL, COMPANY, hasPlaceholders } from '@/lib/legal/companyDetails';

/**
 * Impressum — the operator disclosure for Studio RRB OÜ.
 *
 * Estonian law applies (Äriseadustik §15¹, Infoühiskonna teenuse seadus §4); see
 * lib/legal/companyDetails for why serving Austria and Germany does not change that.
 * The facts come from that file, the labels from the legal namespace.
 *
 * Rows whose value is empty are dropped, so an optional detail the company does not
 * publish leaves no blank line behind.
 */
function Row({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 sm:col-span-2">{value}</dd>
    </div>
  );
}

export default function ImpressumPage() {
  const { t } = useTranslation('legal');
  const c = COMPANY;

  const address = [c.street, [c.postalCode, c.city].filter(Boolean).join(' '), c.county, c.country]
    .filter(Boolean)
    .join(', ');

  return (
    <LandingLayout>
      <SEOHead
        title={t('impressum.title', 'Impressum')}
        description={t('impressum.seoDescription')}
        canonical="https://slotyapp.com/impressum"
      />
      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex items-center gap-3">
            <Building2 className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('impressum.title', 'Impressum')}</h1>
              <p className="text-sm text-gray-500">{t('impressum.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border p-6 sm:p-8 space-y-8">
            {/* Only appears if a detail is ever left scaffolded again. */}
            {hasPlaceholders() && (
              <div className="rounded-r-lg border-l-4 border-amber-500 bg-amber-50 p-4">
                <p className="text-sm font-medium text-amber-900">{t('impressum.incomplete')}</p>
              </div>
            )}

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">{t('impressum.sections.operator.title')}</h2>
              <dl className="divide-y divide-gray-100">
                <Row label={t('impressum.fields.legalName')} value={c.legalName} />
                <Row label={t('impressum.fields.representative')} value={c.representative} />
                <Row label={t('impressum.fields.address')} value={address} />
              </dl>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">{t('impressum.sections.contact.title')}</h2>
              <p className="text-gray-600">{t('impressum.sections.contact.content')}</p>
              <dl className="divide-y divide-gray-100">
                <Row label={t('impressum.fields.phone')} value={c.phone} />
              </dl>
              <p className="text-sm">
                <span className="font-medium text-gray-500">{t('impressum.fields.email')}: </span>
                <a className="text-blue-600 hover:underline" href={`mailto:${c.email}`}>{c.email}</a>
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">{t('impressum.sections.register.title')}</h2>
              <dl className="divide-y divide-gray-100">
                <Row label={t('impressum.fields.registryCode')} value={c.registryCode} />
                <Row label={t('impressum.fields.vatId')} value={c.vatId} />
                <Row label={t('impressum.fields.jurisdiction')} value={c.jurisdiction} />
              </dl>
              <p className="text-sm text-gray-600">
                {t('impressum.sections.register.content')}{' '}
                <a className="text-blue-600 hover:underline" href={BUSINESS_REGISTER_URL} target="_blank" rel="noopener noreferrer">
                  {t('impressum.sections.register.registerLink')}
                </a>
              </p>
            </section>

            {/* Not required by Estonian law, but an Austrian or German reader expects a
                line on what the site is for, in the spirit of §25 MedienG. */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">{t('impressum.sections.purpose.title')}</h2>
              <p className="text-gray-600">{t('impressum.sections.purpose.content')}</p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">{t('impressum.sections.disputeResolution.title')}</h2>
              <p className="text-gray-600">{t('impressum.sections.disputeResolution.content')}</p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">{t('impressum.sections.liability.title')}</h2>
              <p className="text-gray-600">{t('impressum.sections.liability.links')}</p>
              <p className="text-gray-600">{t('impressum.sections.liability.copyright')}</p>
            </section>
          </div>
        </div>
      </div>
    </LandingLayout>
  );
}
